import { useEffect, useState } from "react";
import { CheckCircle2, XCircle, FileText, Wrench, Image as ImageIcon } from "lucide-react";
import { Reveal } from "../lib/motion";
import { api, getFragmentToken } from "../lib/api";

const money = (c) => `$${(c / 100).toFixed(2)}`;

function withToken(path, token) {
  return { url: `${process.env.REACT_APP_BACKEND_URL || ""}${path}`, headers: { "X-Access-Token": token } };
}

export function CustomerAccess() {
  const token = getFragmentToken("ccc_customer_token");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [responses, setResponses] = useState({});
  const [name, setName] = useState("");
  const [flash, setFlash] = useState("");

  useEffect(() => {
    if (!token) { setError("This page needs your access link. Please use the link the shop sent you."); return; }
    const { url, headers } = withToken("/api/customer/access", token);
    fetch(url, { headers }).then(async (r) => {
      const body = await r.json();
      if (!r.ok) throw new Error(body.detail || "Link problem");
      setData(body);
    }).catch((e) => setError(e.message));
  }, [token]);

  const respond = async (est) => {
    setFlash("");
    try {
      await api("/customer/estimate-response", { method: "POST", body: {
        token, estimate_id: est.id, confirm_name: name, consent: true,
        item_responses: est.items.map((it) => ({ item_id: it.id, decision: responses[it.id] || "pending" })),
      } });
      setFlash("Response recorded. Staff review it before performing any work.");
      const { url, headers } = withToken("/api/customer/access", token);
      const body = await (await fetch(url, { headers })).json();
      setData(body);
    } catch (e) {
      setError(e.message);
    }
  };

  if (error) return <main className="section container" style={{ maxWidth: 640 }}><div className="error-box" data-testid="customer-error">{error}</div></main>;
  if (!data) return <main className="section container"><div className="skeleton" style={{ height: 300 }} /></main>;
  const { ticket, findings, estimates, invoice } = data;
  const activeEst = [...estimates].reverse().find((e) => e.status === "sent");

  return (
    <main className="section container" style={{ maxWidth: 860 }}>
      <Reveal>
        <span className="eyebrow red">Your vehicle · private view</span>
        <h1 style={{ fontSize: "clamp(26px,3.6vw,42px)", margin: "14px 0 8px" }} data-testid="customer-heading">Ticket {ticket.number}</h1>
        <p className="muted" data-testid="customer-vehicle">{ticket.vehicle.year} {ticket.vehicle.make} {ticket.vehicle.model}{ticket.vehicle.vin ? ` · VIN ${ticket.vehicle.vin}` : ""}</p>
        <p><span className={`pill ${ticket.status}`}>{String(ticket.status).replace(/_/g, " ")}</span>{" "}
          <span className={`pill ${ticket.payment?.paid ? "paid" : ""}`}>{ticket.payment?.paid ? "paid" : "payment at register"}</span></p>
      </Reveal>

      {ticket.repair_record && ticket.repair_record.outcome && (
        <Reveal><div className="card" style={{ padding: 26, marginTop: 20 }} data-testid="customer-repair-summary">
          <h3 style={{ fontSize: 17, marginBottom: 10 }}><Wrench size={15} style={{ verticalAlign: -3 }} /> Repair summary</h3>
          {Object.entries({ confirmed_cause: "Confirmed cause", work: "Work performed", parts: "Parts", final_tests: "Final tests", outcome: "Outcome" }).map(([k, l]) => ticket.repair_record[k] && (
            <p key={k} style={{ margin: "6px 0", fontSize: 14.5 }}><span className="muted">{l}:</span> {ticket.repair_record[k]}</p>
          ))}
        </div></Reveal>
      )}

      {findings.length > 0 && (
        <Reveal><div className="card" style={{ padding: 26, marginTop: 18 }} data-testid="customer-findings">
          <h3 style={{ fontSize: 17, marginBottom: 12 }}><ImageIcon size={15} style={{ verticalAlign: -3 }} /> Shared inspections</h3>
          {findings.map((f) => (
            <div key={f.id} style={{ borderTop: "1px solid var(--line)", padding: "12px 0" }}>
              <strong>{f.title}</strong>
              <p className="muted" style={{ fontSize: 14, margin: "6px 0" }}>{f.description}</p>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                {f.media.map((m) => m.content_type === "video/mp4"
                  ? <video key={m.id} src={`/api/media/${m.id}`} controls style={{ maxWidth: 240, borderRadius: 10 }} />
                  : <img key={m.id} src={`/api/media/${m.id}`} alt={f.title} style={{ maxWidth: 240, borderRadius: 10 }} />)}
              </div>
            </div>
          ))}
        </div></Reveal>
      )}

      {estimates.length > 0 && (
        <Reveal><div className="card" style={{ padding: 26, marginTop: 18 }} data-testid="customer-estimates">
          <h3 style={{ fontSize: 17, marginBottom: 12 }}><FileText size={15} style={{ verticalAlign: -3 }} /> Estimates</h3>
          {estimates.map((est) => (
            <div key={est.id} style={{ borderTop: "1px solid var(--line)", padding: "12px 0" }}>
              <strong>Revision {est.revision}</strong> <span className={`pill ${est.status}`}>{est.status}</span>
              <table className="data" style={{ marginTop: 8 }}>
                <tbody>
                  {est.items.map((it) => (
                    <tr key={it.id}>
                      <td>{it.description}<br /><span className="mono muted" style={{ fontSize: 12 }}>{it.qty} × {money(it.rate_cents)}</span></td>
                      <td><span className={`pill ${it.decision === "approved" ? "paid" : it.decision === "declined" ? "cancelled" : ""}`}>{it.decision}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {est.id === activeEst?.id && (
                <div style={{ marginTop: 10 }}>
                  {est.items.map((it) => (
                    <div key={it.id} style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 6 }} data-testid={`estimate-item-${it.id}`}>
                      <span style={{ flex: 1, fontSize: 13.5 }}>{it.description}</span>
                      <button className={`btn btn-sm ${responses[it.id] === "approved" ? "btn-blue" : "btn-ghost"}`}
                        onClick={() => setResponses({ ...responses, [it.id]: "approved" })} data-testid={`estimate-approve-${it.id}`}>
                        <CheckCircle2 size={13} /> Approve
                      </button>
                      <button className={`btn btn-sm ${responses[it.id] === "declined" ? "btn-red" : "btn-ghost"}`}
                        onClick={() => setResponses({ ...responses, [it.id]: "declined" })} data-testid={`estimate-decline-${it.id}`}>
                        <XCircle size={13} /> Decline
                      </button>
                    </div>
                  ))}
                  <div className="field" style={{ marginTop: 10 }}><label>Confirm your name</label>
                    <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Type your full name" data-testid="estimate-confirm-name" /></div>
                  <p className="form-note">Responding records your decisions for the shop to review. This is not a payment and does not start work automatically.</p>
                  <button className="btn btn-red btn-sm" onClick={() => respond(est)} disabled={!name.trim()} data-testid="estimate-submit-response">Submit my decisions</button>
                </div>
              )}
            </div>
          ))}
        </div></Reveal>
      )}

      {invoice && (
        <Reveal><div className="card" style={{ padding: 26, marginTop: 18 }} data-testid="customer-invoice">
          <h3 style={{ fontSize: 17, marginBottom: 10 }}>Invoice {invoice.number}</h3>
          <table className="data">
            <tbody>
              {invoice.lines.map((l) => (
                <tr key={l.id}><td>{l.description} <span className="muted">({l.kind})</span></td>
                  <td className="mono">{l.qty} × {money(l.rate_cents)}</td>
                  <td className="mono">{money(Math.round(l.qty * l.rate_cents))}</td></tr>
              ))}
              <tr><td><strong>Total{invoice.tax_rate_percent ? ` (incl. ${invoice.tax_rate_percent}% tax)` : ""}</strong></td>
                <td></td><td className="mono"><strong>{money(invoice.totals.total_cents)}</strong></td></tr>
            </tbody>
          </table>
          <p className="form-note" style={{ marginTop: 10 }}>Payment is handled at our in-store register — no online payments.</p>
        </div></Reveal>
      )}

      {flash && <div className="ok-box" style={{ marginTop: 16 }} data-testid="customer-flash">{flash}</div>}
    </main>
  );
}

export function DealerPortal() {
  const token = getFragmentToken("ccc_dealer_token");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ customer_name: "", customer_phone_or_email: "", vehicle_year_make_model: "", symptoms: "", recent_repairs: "", preferred_visit: "", permission_to_contact_customer: false });

  const load = () => {
    if (!token) { setError("Use your private portal link from the shop."); return; }
    fetch(`${process.env.REACT_APP_BACKEND_URL || ""}/api/dealer/portal`, { headers: { "X-Dealer-Token": token } })
      .then(async (r) => { const b = await r.json(); if (!r.ok) throw new Error(b.detail || "Portal link problem"); setData(b); })
      .catch((e) => setError(e.message));
  };
  useEffect(load, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await api("/dealer/referrals", { method: "POST", headers: { "X-Dealer-Token": token }, body: form });
      setForm({ customer_name: "", customer_phone_or_email: "", vehicle_year_make_model: "", symptoms: "", recent_repairs: "", preferred_visit: "", permission_to_contact_customer: false });
      load();
    } catch (e2) {
      setError(e2.message);
    }
  };

  if (error) return <main className="section container" style={{ maxWidth: 640 }}><div className="error-box" data-testid="dealer-portal-error">{error}</div></main>;
  if (!data) return <main className="section container"><div className="skeleton" style={{ height: 300 }} /></main>;

  return (
    <main className="section container" style={{ maxWidth: 860 }}>
      <Reveal>
        <span className="eyebrow red">Dealer referral portal</span>
        <h1 style={{ fontSize: "clamp(26px,3.6vw,40px)", margin: "14px 0 8px" }} data-testid="dealer-portal-heading">{data.dealer_name}</h1>
        <p className="muted">Private referrals only. You see the referrals you submitted — never customer repair history.</p>
      </Reveal>
      <Reveal><div className="card" style={{ padding: 26, marginTop: 20 }} data-testid="dealer-referral-form">
        <h3 style={{ fontSize: 17, marginBottom: 14 }}>New referral</h3>
        <form onSubmit={submit}>
          <div className="field-pair">
            <div className="field"><label>Customer name</label><input required value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} data-testid="referral-name-input" /></div>
            <div className="field"><label>Phone or email</label><input required value={form.customer_phone_or_email} onChange={(e) => setForm({ ...form, customer_phone_or_email: e.target.value })} /></div>
          </div>
          <div className="field"><label>Vehicle (year make model)</label><input required value={form.vehicle_year_make_model} onChange={(e) => setForm({ ...form, vehicle_year_make_model: e.target.value })} /></div>
          <div className="field"><label>Symptoms</label><textarea required rows={2} value={form.symptoms} onChange={(e) => setForm({ ...form, symptoms: e.target.value })} /></div>
          <div className="field-pair">
            <div className="field"><label>Recent repairs (optional)</label><input value={form.recent_repairs} onChange={(e) => setForm({ ...form, recent_repairs: e.target.value })} /></div>
            <div className="field"><label>Preferred visit (optional)</label><input value={form.preferred_visit} onChange={(e) => setForm({ ...form, preferred_visit: e.target.value })} /></div>
          </div>
          <label className="consent">
            <input type="checkbox" required checked={form.permission_to_contact_customer} onChange={(e) => setForm({ ...form, permission_to_contact_customer: e.target.checked })} />
            <span>Customer authorized C.C.C. Auto Electric to contact them about this referral.</span>
          </label>
          <button className="btn btn-blue" data-testid="referral-submit">Send referral</button>
        </form>
      </div></Reveal>
      <div className="card" style={{ padding: 26, marginTop: 18 }} data-testid="dealer-referrals-list">
        <h3 style={{ fontSize: 17, marginBottom: 12 }}>Your referrals ({data.referrals.length})</h3>
        {data.referrals.length === 0 && <p className="muted" style={{ fontSize: 14 }}>No referrals yet.</p>}
        <table className="data">
          <tbody>
            {data.referrals.map((r) => (
              <tr key={r.id}><td><strong>{r.customer_name}</strong><br /><span className="muted" style={{ fontSize: 12.5 }}>{r.vehicle_year_make_model}</span></td>
                <td>{r.symptoms}</td><td><span className={`pill ${r.status}`}>{String(r.status).replace(/_/g, " ")}</span></td></tr>
            ))}
          </tbody>
        </table>
      </div>
    </main>
  );
}
